// The settings item "this PC's agents": what the agent on this PC found (CLIs,
// their login and usage limits, the working directories it may use), logging a
// CLI in from the screen, and the defaults of a new chat.
//
// Promise:
// - No save buttons: a choice is sent when made, a number on blur or Enter;
//   nothing is sent when nothing changed; only a failure shows a line.
// - The directories are what the agent reports; they are not added here.
// - Login: the agent opens the CLI's login and reports a URL (and Codex's
//   code); Claude's code is pasted back here. The state comes from the agent.
// - Usage limits can be turned off for the device (only where the host asks
//   for the row: the offline edition's own PC). Off, every CLI says so instead
//   of showing bars.
import { useQuery } from "@tanstack/react-query";
import type { AgentDeviceDTO } from "@trameria/core/agent-chat-shared";
import { m } from "@trameria/i18n";
import type { AgentCapability, DeviceSettings } from "@trameria/shared-agent";
import { useEffect, useRef, useState } from "react";
import { useApi, useMeApp } from "../app/context";
import type { SettingsItem } from "../app/screen-part";
import { apiErrorMessage } from "../data/errors";
import {
  SettingsBox,
  SettingsRow,
  SettingsSection,
} from "../settings/settings-page";
import { Choices, ErrorLine, RowInput, Skeleton, Switch } from "../ui/bits";
import { Button } from "../ui/button";
import { iconOf } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { SelectMenu } from "../ui/menu";
import { agentOf, capabilitiesOf, cwdOptions, settingsOf } from "./conditions";
import { RateLimitBars } from "./limits";
import { devicesQuery, useDeviceLogin, useUpdateDevice } from "./queries";

function ThisPc() {
  const devices = useQuery(devicesQuery(useApi()));
  // In a list with no `this_pc` (Personal data in Cloud), the app names this
  // PC's own device; never the list's first, which is another PC's.
  const thisDevice = useMeApp().extensions.thisDevice;
  const own = useQuery({
    queryKey: ["me", "agents", "this-device"],
    queryFn: async () => (await thisDevice?.()) ?? null,
    enabled: thisDevice !== undefined,
  });
  if (devices.isPending || (thisDevice && own.isPending))
    return <Skeleton lines={[60, 40, 70]} />;
  if (devices.isError)
    return (
      <ErrorLine>
        {apiErrorMessage(devices.error, m.agents_load_failed())}
      </ErrorLine>
    );
  const device =
    devices.data.find((d) => d.kind === "this_pc") ??
    (thisDevice
      ? devices.data.find((d) => d.id === own.data)
      : devices.data[0]);
  if (!device)
    return (
      <p className="mt-6 text-[13px] text-faint">{m.agents_no_device()}</p>
    );
  return <DeviceSettingsView key={device.id} device={device} usageSetting />;
}

/** One device's agents, working directories and defaults (also used by the desktop app's device list). */
export function DeviceSettingsView({
  device,
  usageSetting = false,
}: {
  device: AgentDeviceDTO;
  /** Shows the switch that turns the usage limits off for the device. */
  usageSetting?: boolean;
}) {
  const caps = capabilitiesOf(device);
  const settings = settingsOf(device);
  const update = useUpdateDevice();
  const [name, setName] = useState(device.name);
  const save = (patch: Partial<DeviceSettings>) =>
    update.mutate({ device, settings: patch });
  const agent = agentOf(caps, settings.agent);
  return (
    <>
      <SettingsBox>
        <SettingsRow label={m.agents_pc_name()}>
          <RowInput
            aria-label={m.agents_pc_name()}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              const n = name.trim();
              if (n && n !== device.name) update.mutate({ device, name: n });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isImeComposing(e.nativeEvent))
                e.currentTarget.blur();
            }}
          />
        </SettingsRow>
      </SettingsBox>
      {update.isError && (
        <ErrorLine>
          {apiErrorMessage(update.error, m.agents_save_failed())}
        </ErrorLine>
      )}

      <SettingsSection id="agents" label={m.agents_pc_agents()}>
        {usageSetting && (
          <SettingsBox>
            <SettingsRow
              id="usage-limits"
              label={m.agents_pc_usage_limits()}
              description={m.agents_pc_usage_limits_note()}
            >
              <Switch
                label={m.agents_pc_usage_limits()}
                checked={settings.usageLimits}
                onChange={(on) => save({ usageLimits: on })}
              />
            </SettingsRow>
          </SettingsBox>
        )}
        {caps.agents.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-faint">
            {m.agents_no_agent_yet()}
          </p>
        ) : (
          caps.agents.map((a) => (
            <AgentBox
              key={a.kind}
              device={device}
              agent={a}
              usageOff={!settings.usageLimits}
            />
          ))
        )}
      </SettingsSection>

      <SettingsSection id="workdirs" label={m.agents_pc_workdirs()}>
        <p className="mt-1 text-[11.5px] leading-[1.6] text-faint">
          {m.agents_pc_workdirs_note()}
        </p>
        <SettingsBox>
          {caps.workdirs.length === 0 ? (
            <div className="px-4 py-3 text-[12.5px] text-faint">
              {m.agents_pc_workdirs_none()}
            </div>
          ) : (
            caps.workdirs.map((w) => (
              <div
                key={w.path}
                className="flex items-baseline gap-3 px-4 py-2.5"
              >
                <span className="text-[13px] text-fg">{w.name}</span>
                <span className="min-w-0 truncate font-mono text-[11.5px] text-faint">
                  {w.path}
                </span>
              </div>
            ))
          )}
        </SettingsBox>
      </SettingsSection>

      <SettingsSection id="defaults" label={m.agents_pc_defaults()}>
        <SettingsBox>
          {caps.agents.length > 1 && (
            <SettingsRow label={m.agents_pc_default_agent()}>
              <Choices
                label={m.agents_pc_default_agent()}
                value={settings.agent}
                options={caps.agents.map((a) => ({
                  value: a.kind,
                  label: a.label,
                }))}
                onChange={(kind) => {
                  const next = agentOf(caps, kind);
                  save({ agent: kind, model: next?.defaultModel ?? null });
                }}
              />
            </SettingsRow>
          )}
          <SettingsRow label={m.agents_pc_default_model()}>
            <SelectMenu
              label={m.agents_pc_default_model()}
              value={settings.model ?? ""}
              options={[
                { value: "", label: m.agents_model_cli_default() },
                ...(agent?.models.map((x) => ({
                  value: x.id,
                  label: x.label,
                })) ?? []),
                ...(settings.model !== null &&
                !agent?.models.some((x) => x.id === settings.model)
                  ? [
                      {
                        value: settings.model,
                        label: m.agents_value_missing({
                          value: settings.model,
                        }),
                        muted: true,
                      },
                    ]
                  : []),
              ]}
              onChange={(v) => save({ model: v || null })}
            />
          </SettingsRow>
          <SettingsRow
            label={m.agents_pc_default_cwd()}
            description={m.agents_pc_default_cwd_note()}
          >
            <SelectMenu
              label={m.agents_pc_default_cwd()}
              value={settings.cwd ?? ""}
              options={[
                { value: "", label: m.agents_cwd_none() },
                ...cwdOptions(caps, settings).map((o) => ({
                  value: o.path,
                  label: o.label,
                })),
              ]}
              onChange={(v) => save({ cwd: v || null })}
            />
          </SettingsRow>
          <SettingsRow
            label={m.agents_pc_concurrency()}
            description={m.agents_pc_concurrency_note()}
          >
            <Choices<"parallel" | "serial">
              label={m.agents_pc_concurrency()}
              value={settings.concurrency}
              options={[
                { value: "parallel", label: m.agents_pc_parallel() },
                { value: "serial", label: m.agents_pc_serial() },
              ]}
              onChange={(v) => save({ concurrency: v })}
            />
          </SettingsRow>
          <SettingsRow
            label={m.agents_pc_quiet()}
            description={m.agents_pc_quiet_note()}
          >
            <NumberField
              label={m.agents_pc_quiet()}
              value={settings.quietMs / 1000}
              min={0.5}
              max={60}
              step={0.5}
              onSave={(v) => save({ quietMs: Math.round(v * 1000) })}
            />
          </SettingsRow>
          {(["claude", "codex"] as const)
            .filter((k) => agentOf(caps, k))
            .map((k) => (
              <SettingsRow
                key={k}
                label={m.agents_pc_compact({
                  agent: agentOf(caps, k)?.label ?? k,
                })}
                description={m.agents_pc_compact_note()}
              >
                <NumberField
                  label={m.agents_pc_compact({
                    agent: agentOf(caps, k)?.label ?? k,
                  })}
                  value={settings.compactPercent[k] ?? 100}
                  min={30}
                  max={100}
                  step={5}
                  onSave={(v) =>
                    save({
                      compactPercent: { ...settings.compactPercent, [k]: v },
                    })
                  }
                />
              </SettingsRow>
            ))}
        </SettingsBox>
      </SettingsSection>
    </>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  step,
  onSave,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onSave(v: number): void;
}) {
  const [text, setText] = useState(String(value));
  const commit = () => {
    const v = Number(text);
    if (!Number.isFinite(v) || v < min || v > max) {
      setText(String(value));
      return;
    }
    if (v !== value) onSave(v);
  };
  return (
    <RowInput
      type="number"
      aria-label={label}
      value={text}
      min={min}
      max={max}
      step={step}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !isImeComposing(e.nativeEvent))
          e.currentTarget.blur();
      }}
      className="w-[96px]"
    />
  );
}

function authWords(agent: AgentCapability): string {
  const a = agent.auth;
  if (!a) return m.agents_pc_auth_unknown();
  if (a.state === "ok")
    return a.account
      ? m.agents_pc_auth_ok_account({ account: a.account })
      : m.agents_pc_auth_ok();
  if (a.state === "loggedOut") return m.agents_pc_auth_out();
  return m.agents_pc_auth_unknown();
}

/** One CLI: version, login and usage limits. */
function AgentBox({
  device,
  agent,
  usageOff,
}: {
  device: AgentDeviceDTO;
  agent: AgentCapability;
  usageOff: boolean;
}) {
  return (
    <SettingsBox>
      <SettingsRow label={agent.label} description={agent.version}>
        <span
          className={`text-[12.5px] ${agent.auth?.state === "loggedOut" ? "text-danger" : "text-muted"}`}
        >
          {authWords(agent)}
        </span>
      </SettingsRow>
      <div className="px-4 py-3">
        <LoginPanel device={device} agent={agent} />
      </div>
      <div className="px-4 py-3">
        <div className="mb-2 text-[12px] text-muted">
          {m.agents_limit_title()}
        </div>
        <RateLimitBars limits={agent.rateLimits} off={usageOff} />
      </div>
    </SettingsBox>
  );
}

function LoginPanel({
  device,
  agent,
}: {
  device: AgentDeviceDTO;
  agent: AgentCapability;
}) {
  const login = useDeviceLogin();
  const [code, setCode] = useState("");
  const [sentCode, setSentCode] = useState(false);
  const state = agent.login;
  // Pressed here, the next step (the link, the code, the field) grows below
  // the button and can fall past the bottom of a phone's screen: brought into
  // view as the step moves on, clear of the floating buttons.
  const box = useRef<HTMLDivElement>(null);
  const pressed = useRef(false);
  const stepKey = `${state?.state ?? ""}:${state?.userCode ?? ""}:${state?.needsCode ?? ""}`;
  // frontend-development: not a fetch; moves the box into view after a step.
  useEffect(() => {
    if (pressed.current && stepKey)
      box.current?.scrollIntoView({ block: "nearest" });
  }, [stepKey]);
  const send = (
    command: { action: "start" | "cancel" } | { action: "code"; code: string },
  ) => {
    pressed.current = true;
    login.mutate({
      deviceId: device.id,
      command: {
        ...command,
        kind: agent.kind,
        requestId:
          command.action === "start"
            ? crypto.randomUUID()
            : (state?.requestId ?? ""),
      } as never,
    });
  };
  const failed = login.isError && (
    <ErrorLine>
      {apiErrorMessage(login.error, m.agents_pc_login_failed())}
    </ErrorLine>
  );
  if (!state)
    return (
      <div
        ref={box}
        className="flex scroll-mb-20 items-center gap-3 text-[12px]"
      >
        <Button
          variant="outline"
          disabled={login.isPending}
          onClick={() => send({ action: "start" })}
        >
          {agent.auth?.state === "ok"
            ? m.agents_pc_login_again()
            : m.agents_pc_login()}
        </Button>
        {login.isSuccess && (
          <span className="text-faint">{m.agents_pc_login_preparing()}</span>
        )}
        {failed}
      </div>
    );
  if (state.state === "failed")
    return (
      <div ref={box} className="flex scroll-mb-20 flex-col gap-2 text-[12px]">
        <span className="text-danger">
          {m.agents_pc_login_error({ error: state.error ?? "" })}
        </span>
        <div>
          <Button variant="outline" onClick={() => send({ action: "start" })}>
            {m.agents_retry()}
          </Button>
        </div>
        {failed}
      </div>
    );
  if (state.state === "verifying")
    return (
      <div ref={box} className="scroll-mb-20 text-[12px] text-faint">
        {m.agents_pc_login_verifying()}
      </div>
    );
  return (
    <div ref={box} className="flex scroll-mb-20 flex-col gap-2 text-[12px]">
      <div className="flex flex-wrap items-center gap-3">
        {state.url && (
          <a
            href={state.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              if (state.userCode)
                void navigator.clipboard?.writeText(state.userCode);
            }}
            className="inline-flex h-8 items-center rounded-2xl border border-accent bg-accent px-[13px] text-[13px] text-accent-fg hover:bg-accent-hover"
          >
            {state.userCode
              ? m.agents_pc_login_open_copy()
              : m.agents_pc_login_open()}
          </a>
        )}
        <button
          type="button"
          onClick={() => send({ action: "cancel" })}
          className="ml-auto cursor-pointer text-muted hover:text-body"
        >
          {m.agents_cancel()}
        </button>
      </div>
      {state.userCode && (
        <div className="flex items-center gap-3">
          <span className="font-mono text-[17px] tracking-[0.08em] text-fg">
            {state.userCode}
          </span>
          <span className="text-faint">{m.agents_pc_login_code_hint()}</span>
        </div>
      )}
      {state.needsCode && (
        <div className="flex flex-col gap-1.5">
          <span className="text-faint">{m.agents_pc_login_paste_hint()}</span>
          <div className="flex gap-2">
            <RowInput
              aria-label={m.agents_pc_login_code()}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="font-mono"
            />
            <Button
              variant="outline"
              disabled={code.trim() === "" || login.isPending}
              onClick={() => {
                send({ action: "code", code: code.trim() });
                setSentCode(true);
              }}
            >
              {m.agents_send()}
            </Button>
          </div>
          {sentCode && (
            <span className="text-faint">{m.agents_pc_login_sent()}</span>
          )}
        </div>
      )}
      {failed}
    </div>
  );
}

export const thisPcSettings: readonly SettingsItem[] = [
  {
    id: "agents",
    order: 40,
    label: () => m.agents_pc_title(),
    icon: iconOf("agent"),
    description: () => m.agents_pc_description(),
    sections: [
      { id: "agents", label: () => m.agents_pc_agents() },
      { id: "usage-limits", label: () => m.agents_pc_usage_limits() },
      { id: "workdirs", label: () => m.agents_pc_workdirs() },
      { id: "defaults", label: () => m.agents_pc_defaults() },
    ],
    Component: ThisPc,
  },
];
